import type React from 'react';
import type { SavedListing } from 'app/shared/halo/contexts/SavedListingsContext';
import * as styles from './savedListingCard.styles';

type SavedListingCardProps = {
  listing: SavedListing;
  onRemove: (id: string) => void;
  onClick?: () => void;
};

export const SavedListingCard = ({ listing, onRemove, onClick }: SavedListingCardProps) => {
  const formatPrice = (amount: number): string => {
    return `$${amount.toLocaleString('en-US')}/mo`;
  };

  const buildDetailsText = (): string => {
    const parts: Array<string> = [];
    if (listing.bedDisplay) parts.push(listing.bedDisplay);
    if (listing.bathDisplay) parts.push(listing.bathDisplay);
    if (listing.neighborhood) parts.push(listing.neighborhood);
    return parts.join(' \u2022 ');
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove(listing.id);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if ((e.key === 'Enter' || e.key === ' ') && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div className={styles.cardContainer}>
      <div
        className={styles.cardContent}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={onClick ? 0 : undefined}
        role={onClick ? 'button' : undefined}
        aria-label={onClick ? `View details for ${listing.title}` : undefined}
      >
        <img src={listing.imageUrl} alt={listing.title} className={styles.cardImage} />
        <div className={styles.cardInfo}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitlePriceContainer}>
              <div className={styles.cardTitle}>{listing.title}</div>
              <div className={styles.cardPrice}>{formatPrice(listing.price)}</div>
            </div>
            <button
              className={styles.removeButton}
              onClick={handleRemove}
              aria-label={`Remove ${listing.title} from saved listings`}
              type="button"
            >
              Remove
            </button>
          </div>
          <div className={styles.cardDetails}>{buildDetailsText()}</div>
          {listing.topAmenities && listing.topAmenities.length > 0 && (
            <div className={styles.amenitiesContainer}>
              {listing.topAmenities.slice(0, 2).map((amenity) => (
                <span key={amenity} className={styles.amenityPill}>
                  {amenity}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
